import { assertNotEmpty } from '@festina-life/core'

import type { MetaModel } from '../../types/metaModel'
import type {
  AbstractTemplate,
  DeclaredConfigurationTemplateFunctionValue,
  DynamicConfigurationTemplateFunctionValue,
  SubTemplateFieldValue,
  SubTemplateValueSetupField,
  SubTemplateValueSetupFieldValue,
  TemplateVersion,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'

export const addDynamicFunctionToSubTemplatedField = ({
  subTemplatedField,
  dynamicFunction,
  subTemplateIdentifier,
  metaModel,
}: {
  subTemplatedField: SubTemplateValueSetupField
  subTemplateIdentifier: string
  metaModel: MetaModel
  dynamicFunction: DynamicConfigurationTemplateFunctionValue
}) => {
  const subTemplatedFieldValue =
    subTemplatedField.value ??
    generateEmptyFieldValue({
      field: subTemplatedField,
      meta: metaModel,
    })
  const updatedVariant = subTemplatedFieldValue.variants.find(
    (variant) => variant.subTemplateIdentifier === subTemplateIdentifier,
  )
  assertNotEmpty(updatedVariant)
  assertNotEmpty(subTemplatedFieldValue.variants)

  const indexOfVariant = subTemplatedFieldValue.variants.indexOf(updatedVariant)

  const variantWithFunction = addOrReplaceDynamicFunctionOnTemplate(
    updatedVariant,
    dynamicFunction,
  )
  const nextVariant = {
    ...variantWithFunction,
    origin: updatedVariant.path,
  } satisfies SubTemplateFieldValue

  const nextVariantsArray = subTemplatedFieldValue.variants.toSpliced(
    indexOfVariant,
    1,
    nextVariant,
  )
  const nextFieldValue = {
    ...subTemplatedFieldValue,
    variants: nextVariantsArray,
  }

  ;(subTemplatedField.update as (v: SubTemplateValueSetupFieldValue) => void)(
    nextFieldValue,
  )
}

export const addOrReplaceDynamicFunctionOnTemplate = <
  T extends AbstractTemplate | TemplateVersion,
>(
  template: T,
  newFunction: DynamicConfigurationTemplateFunctionValue,
): T => {
  const overridenDeclaredFunctionIndex = (
    template.declaredFunctions ?? []
  ).findIndex(
    (declaredFunction) =>
      declaredFunction.functionIdentifier === newFunction.functionIdentifier,
  )
  const overridenDynamicFunctionIndex =
    overridenDeclaredFunctionIndex === -1
      ? (template.dynamicFunctions ?? []).findIndex(
          (dynamicFunction) =>
            dynamicFunction.functionIdentifier ===
            newFunction.functionIdentifier,
        )
      : -1

  return {
    ...template,
    dynamicFunctions:
      overridenDynamicFunctionIndex !== -1
        ? template.dynamicFunctions?.toSpliced(
            overridenDynamicFunctionIndex,
            1,
            newFunction,
          )
        : [
            ...(template.dynamicFunctions ?? []),
            ...(overridenDeclaredFunctionIndex !== -1 ? [] : [newFunction]),
          ],
    declaredFunctions:
      overridenDeclaredFunctionIndex !== -1
        ? template.declaredFunctions?.toSpliced(
            overridenDeclaredFunctionIndex,
            1,
            {
              ...newFunction,
              type: 'declared',
              descriptor: null,
              update: () => {},
            } satisfies DeclaredConfigurationTemplateFunctionValue,
          )
        : template.declaredFunctions,
  }
}
